import React, { ReactNode } from 'react';
import { RootState } from '../../../redux/store';
import { changeStruct, changeStructDirty, changeStructIsUpdate, clearStruct } from '../../../redux/actions/StructActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import StructEditorComponent from './StructEditorComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomLoading from '../../custom/CustomLoading';
import ComponentName from '../../../constant/ComponentName';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import CollectionNames from '../../../constant/CollectionNames';
import { DataStructure } from '../../../domain/structure/DataStructureDomains';
import StructService from '../../../service/StructService';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import {
  ArrayVariableProperties,
  CustomStructureProperties,
  DateVariableProperties,
  ObjectVariableProperties,
  VariableProperties,
} from '../../../domain/datatype/VariablePropertiesDomains';
import Breadcrumb from '../../Breadcrumb';
import RoleHelper from '../../../RoleHelper';
import { clearPage } from '../../../redux/actions/PageActions';
import DataType from '../../../constant/DataType';
import CustomTabv2 from '../../custom/CustomTabv2';
import ComponentAuditLog from '../../ComponentAuditLog';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import StructAccessComponent from './StructAccessComponent';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import eq = query.eq;
import and = query.and;
import { ConfirmPopup } from 'primereact/confirmpopup';

class StructEditorContainer extends StandardEditorContainerComponent<Props, State, DataStructure> {
  public state = new State();

  private structSubscription: subscribe.Subscription | undefined;
  private componentDependencySubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private structService: StructService = ServiceContainer.getInstance().get(ServiceNames.STRUCT);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.STRUCT,
      icon: 'struct',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const { loading } = this.state;
    const {
      struct: { struct },
      isUpdate,
    } = this.props;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col overflow-y-hidden w-full h-full'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={struct.name}
          description={struct.description}
          status={'DRAFT'}
          statusVisible={false}
          isUpdate={isUpdate}
          icon={'struct'}
          component={ComponentName.STRUCT}
          onSave={this.onSave}
          onRemove={this.onRemove}
          onChangeName={this.onChangeName}
          onChangeDescription={this.onChangeDescription}
          disableSave={!RoleHelper.canEdit('Struct')}
          disableRemove={!RoleHelper.canEdit('Struct')}
          disableNameChange={!RoleHelper.canEdit('Struct')}
          disableDescriptionChange={!RoleHelper.canEdit('Struct')}
          disableDeploy={!RoleHelper.canDeploy('Struct')}
          disableUndeploy={!RoleHelper.canDeploy('Struct')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearStruct } = this.props;
    clearStruct();
    await super.componentWillUnmount();
  }

  protected _startTracker = async () => {
    const structId = this.getVariableIdFromRoute();
    if (structId == undefined) {
      this.setState({ loading: false });
    }
    const structQuery = query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).where(eq('id', structId));
    this.structSubscription = subscribe.Formica.subscribe(CollectionNames.DATA_STRUCTURES, structQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.structSubscription]);
    if (structId) {
      const dependencyQuery = query.QueryBuilder.select()
        .from(CollectionNames.COMPONENT_DEPENDENCY)
        .where(and(eq('dependentType', ComponentName.STRUCT_FIELD), eq('meta.struct', structId)))
        .build();
      this.componentDependencySubscription = subscribe.Formica.subscribe(CollectionNames.COMPONENT_DEPENDENCY, dependencyQuery);
      this.computation?.addSubscription(this.componentDependencySubscription);
    }
  };

  protected tracker = async () => {
    const { changeStruct, changeStructIsUpdate } = this.props;
    if (this.structSubscription?.ready() && (this.componentDependencySubscription == undefined || this.componentDependencySubscription.ready())) {
      const structId = this.getVariableIdFromRoute();
      if (structId == undefined) {
        changeStructIsUpdate(false);
        this.setState({ loading: false });
        return;
      }
      const structQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).where(eq('id', structId)).build();
      const struct: DataStructure | undefined = await this.repository.find(structQuery);
      if (struct) {
        changeStruct(struct);
        changeStructIsUpdate(true);
        this.prepare();
        this.setState({ loading: false });
      }
    }
  };

  protected _stopTracker = async () => {
    await this.structSubscription?.stop();
    await this.componentDependencySubscription?.stop();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private onChangeName = (name: string) => {
    const {
      struct: { struct },
      changeStruct,
    } = this.props;
    if (isStringValid(name)) {
      struct.name = name.trim();
      this.changeItem(struct);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      struct: { struct },
      changeStruct,
    } = this.props;
    struct.description = description;
    this.changeItem(struct);
  };

  private onSave = async () => {
    const {
      struct: { struct, isUpdate },
      lastCategory,
      changeStructIsUpdate,
      changeStructDirty,
    } = this.props;
    try {
      const isSaved = await this.structService.save(struct, isUpdate, lastCategory);
      if (isSaved) {
        changeStructDirty(false);
        changeStructIsUpdate(true);
        if (!isUpdate) await this.switchToUpdate();
      }
    } catch (e) {
      console.log(e);
    }
  };

  private onRemove = (e: any) => {
    const {
      struct: { struct },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.structService.remove(struct.name);
        if (success) {
          navigate(`/struct`);
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deleteStruct', defaultMessage: 'Are you sure to delete this struct?' }),
    });
  };

  private getVariableIdFromRoute = () => {
    return this.props.router.params.id;
  };

  private switchToUpdate = async () => {
    const {
      struct: { struct },
      enterComponent,
    } = this.props;
    if (struct.name == '') return;
    const {
      router: { navigate, params },
    } = this.props;
    enterComponent({ component: ComponentName.STRUCT, id: struct.name });
    navigate(`/struct${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${struct.name}`, { replace: true });
    await this.resubscribeTracker();
  };

  private getVariableType = async (properties: VariableProperties): Promise<string | number | boolean | Date | Array<any> | Record<string, any>> => {
    switch (properties.dataType) {
      case DataType.STRING:
        return '';
      case DataType.NUMBER:
        return 0;
      case DataType.BOOLEAN:
        return true;
      case DataType.DATE:
        return (properties as DateVariableProperties).format || Date.now();
      case DataType.OBJECT:
        const object: Record<string, any> = {};
        for (const f of (properties as ObjectVariableProperties).variables) {
          object[f.name] = await this.getVariableType(f.properties);
        }
        return object;
      case DataType.ARRAY:
        const item = await this.getVariableType((properties as ArrayVariableProperties).properties);
        return [item];
      case DataType.STRUCTURE:
        const name = (properties as CustomStructureProperties).name;
        const structure: DataStructure | undefined = await this.repository.find(
          query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).where(eq('name', name)).build(),
        );
        if (structure == undefined || structure.properties == undefined) return {};
        return await this.getVariableType(structure.properties);
      default:
        return 'string';
    }
  };

  private prepare = async () => {
    const {
      struct: { struct },
    } = this.props;
    const previewObject: Record<string, any> = (await this.getVariableType(struct.properties)) as Record<string, any>;
    this.setState({ preview: previewObject });
  };

  protected getTabs = () => {
    const { preview } = this.state;
    return [
      {
        key: 'Editor',
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Editor' }),
        content: <StructEditorComponent prepare={this.prepare} preview={preview} />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <StructAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.STRUCT, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.STRUCT} documentName={this.props.struct.struct.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.STRUCT, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  protected changeItem = (item: DataStructure) => {
    const { changeStruct, changeStructDirty } = this.props;
    changeStruct(item);
    changeStructDirty(true);
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  loading: boolean = true;
  preview: Record<string, any> = {};
}

const mapStateToProps = (state: RootState) => ({
  struct: state.struct,
  isUpdate: state.struct.isUpdate,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {
  changeStruct,
  clearStruct,
  changeStructIsUpdate,
  enterComponent,
  leaveComponent,
  clearPage,
  changeStructDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(StructEditorContainer));
